import {
  GamoraBase64URLGuard,
  GamoraErrorUnhandledException,
  assert,
  convertEnumToValues,
} from '@ver.id/libs.internal.gamora';
import { HoruzProtocolChulakCloseCode } from '../../codes.js';
import { HoruzProtocolChulakDataType } from '../../data.js';
import { HoruzProtocolChulakMessageGuard } from './base.js';

/**
 * Input type
 * @public
 */
export interface HoruzProtocolChulakMessageSessionAbortedGuardInput {
  code?: HoruzProtocolChulakCloseCode;
  reason?: string;
}

/**
 * @public
 */
export class HoruzProtocolChulakMessageSessionAbortedGuard extends HoruzProtocolChulakMessageGuard<
  HoruzProtocolChulakDataType.DATA_SESSION_ABORTED,
  HoruzProtocolChulakMessageSessionAbortedGuardInput
> {
  private _code: HoruzProtocolChulakCloseCode =
    HoruzProtocolChulakCloseCode.PROTOCOL_UNKNOWN_ERROR;
  private _reason = '';

  /**
   * Constructor
   * @param input - the input
   */
  constructor(input: HoruzProtocolChulakMessageSessionAbortedGuardInput) {
    super();
    this.code = input.code;
    this.reason = input.reason;
  }

  /**
   * Gets type
   */
  get type(): HoruzProtocolChulakDataType.DATA_SESSION_ABORTED {
    return HoruzProtocolChulakDataType.DATA_SESSION_ABORTED;
  }

  /**
   * AuthenticationCompact
   */
  get code(): HoruzProtocolChulakCloseCode {
    return this._code;
  }
  set code(code: HoruzProtocolChulakCloseCode | undefined) {
    if (code) {
      assert(
        convertEnumToValues(HoruzProtocolChulakCloseCode).includes(code),
        'Unknown code: not a known close code.',
      );
      this._code = code;
    } else {
      this._code = HoruzProtocolChulakCloseCode.PROTOCOL_UNKNOWN_ERROR;
    }
  }

  /**
   * reason
   */
  get reason(): string {
    return this._reason;
  }
  set reason(reason: string | undefined) {
    if (reason) {
      assert(
        typeof reason === 'string',
        'Unknown reason: not a known close reason.',
      );
      this._reason = reason;
    } else {
      this._reason = '';
    }
  }

  /**
   * Gets from JSON
   * @param input - Input string
   */
  static override fromJSON(
    input: unknown,
  ): HoruzProtocolChulakMessageSessionAbortedGuard {
    assert(
      typeof input === 'object' && input !== null,
      'Invalid input argument: not an object.',
    );
    return new HoruzProtocolChulakMessageSessionAbortedGuard(
      input as HoruzProtocolChulakMessageSessionAbortedGuardInput,
    );
  }

  /**
   * Get from base64URL
   * @param input - The input
   */
  static override fromBase64URL(
    input: unknown,
  ): HoruzProtocolChulakMessageSessionAbortedGuard {
    assert(
      typeof input === 'string' && input.length > 0,
      'Invalid input argument: not a valid string.',
    );

    // Parse input
    let json;
    try {
      json = JSON.parse(GamoraBase64URLGuard.fromBase64URL(input).toUTF8());
    } catch {
      throw new GamoraErrorUnhandledException(
        'Unable to parse input: not valid JSON in base64url input.',
      );
    }

    // Create new guard
    return new HoruzProtocolChulakMessageSessionAbortedGuard(
      json as HoruzProtocolChulakMessageSessionAbortedGuardInput,
    );
  }

  /**
   * Gets JSON
   * @returns string
   */
  override toJSON() {
    return {
      code: this._code,
      reason: this._reason,
    };
  }

  /**
   * Create clone
   */
  clone() {
    return HoruzProtocolChulakMessageSessionAbortedGuard.fromJSON(
      this.toJSON(),
    );
  }
}
